if (!customElements.get('step-gamer-pdp')) {
  customElements.define(
    'step-gamer-pdp',
    class StepGamerPdp extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.variantPrice = Number(this.dataset.variantPrice || 0);
        this.months = Math.max(1, Number(this.dataset.months || 12));
        this.currency = this.dataset.currency || 'TRY';
        this.sizeOptionPosition = Number(this.dataset.sizeOptionPosition || 0);
        this.form = this.querySelector('form[data-type="add-to-cart-form"]');
        this.productForm = this.querySelector('product-form');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.querySelectorAll('[data-addon]').forEach((addon) => this.bindAddon(addon));
        this.bindPackages();
        this.bindFeaturesDialog();
        this.bindTaksitDialog();
        this.bindGalleryNav();
        this.bindAccordions();
        this.bindFaqItems();
        this.form?.addEventListener('submit', this.onSubmit.bind(this), true);
        this.form?.addEventListener('click', this.onBuyNowClick.bind(this), true);
        this.bindStickyBar();
        this.updateSubtotal();

        if (typeof subscribe === 'function') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        this.unsubscribeVariant?.();
        this.stickyObserver?.disconnect();
        this.chatObserver?.disconnect();
        this.placeChatBelowBar(false);
        this.stickyBar?.remove();
        document.documentElement.classList.remove('has-pdp-sticky-bar');
      }

      chatLayerNodes() {
        return document.querySelectorAll(
          '#shopify-chat, shopify-chat, #ShopifyChat, iframe#dummy-chat-button-iframe, iframe#ShopifyChat, iframe[name="ShopifyChat"]'
        );
      }

      placeChatBelowBar(active) {
        this.chatLayerNodes().forEach((node) => {
          if (active) {
            node.style.setProperty('z-index', '900', 'important');
            return;
          }
          node.style.removeProperty('z-index');
        });
      }

      bindStickyBar() {
        const bar = this.querySelector('[data-pdp-sticky]');
        const submit = this.querySelector('[data-pdp-submit]');
        const stickySubmit = this.querySelector('[data-pdp-sticky-submit]');
        if (!bar || !submit || !stickySubmit) return;

        document.body.appendChild(bar);
        this.stickyBar = bar;

        const setVisible = (visible) => {
          const show = visible && !submit.disabled;
          bar.classList.toggle('is-visible', show);
          bar.setAttribute('aria-hidden', String(!show));
          document.documentElement.classList.toggle('has-pdp-sticky-bar', show);
          this.placeChatBelowBar(show);
        };

        this.chatObserver = new MutationObserver(() => {
          if (document.documentElement.classList.contains('has-pdp-sticky-bar')) this.placeChatBelowBar(true);
        });
        this.chatObserver.observe(document.body, { childList: true, subtree: true });

        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            setVisible(!entries[0].isIntersecting);
          },
          { threshold: 0.15 }
        );
        this.stickyObserver.observe(submit);
        setVisible(submit.getBoundingClientRect().bottom < 0 || submit.getBoundingClientRect().top > window.innerHeight);

        stickySubmit.addEventListener('click', () => {
          submit.click();
        });
      }

      bindTaksitDialog() {
        const dialog = this.querySelector('[data-taksit-dialog]');
        if (!dialog) return;

        const setOpen = (open) => {
          document.body.classList.toggle('step-gamer-pdp-dialog-open', open);
        };

        this.querySelector('[data-taksit-open]')?.addEventListener('click', () => {
          this.loadPaytrTable();
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
          setOpen(true);
        });
        this.querySelector('[data-taksit-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => setOpen(false));
      }

      getPaytrAmount() {
        return ((this.variantPrice + this.getAddonTotal()) / 100).toFixed(2);
      }

      loadPaytrTable() {
        const host = this.querySelector('[data-taksit-table]');
        const token = this.dataset.paytrToken;
        const merchant = this.dataset.paytrMerchant;
        if (!host || !token || !merchant) return;

        const amount = this.getPaytrAmount();
        if (this.paytrLoadedAmount === amount && host.querySelector('#paytr_taksit_tablosu')?.childElementCount) {
          return;
        }

        this.paytrLoadedAmount = amount;
        const container = host.querySelector('#paytr_taksit_tablosu') || host;
        container.replaceChildren();
        host.querySelectorAll('script[data-paytr-table]').forEach((node) => node.remove());

        const script = document.createElement('script');
        script.dataset.paytrTable = '';
        const params = new URLSearchParams({
          token,
          merchant_id: merchant,
          amount,
          taksit: '0',
          tumu: '0',
        });
        script.src = `https://www.paytr.com/odeme/taksit-tablosu/v2?${params.toString()}`;
        host.appendChild(script);
      }

      bindFeaturesDialog() {
        const dialog = this.querySelector('[data-features-dialog]');
        if (!dialog) return;

        const setOpen = (open) => {
          document.body.classList.toggle('step-gamer-pdp-dialog-open', open);
        };

        this.querySelector('[data-features-open]')?.addEventListener('click', () => {
          if (typeof dialog.showModal === 'function') dialog.showModal();
          else dialog.setAttribute('open', '');
          setOpen(true);
        });
        this.querySelector('[data-features-close]')?.addEventListener('click', () => dialog.close());
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) dialog.close();
        });
        dialog.addEventListener('close', () => setOpen(false));
      }

      bindGalleryNav() {
        const gallery = this.querySelector('media-gallery');
        const viewer = gallery?.querySelector('[id^="GalleryViewer"]');
        const prev = this.querySelector('[data-gallery-prev]');
        const next = this.querySelector('[data-gallery-next]');
        if (!gallery || !viewer || !prev || !next || this.galleryNavBound) return;

        const slides = () => [...viewer.querySelectorAll('.product__media-item')];

        const currentIndex = () => {
          const index = slides().findIndex((slide) => slide.classList.contains('is-active'));
          return Math.max(0, index);
        };

        const syncArrows = () => {
          const items = slides();
          const index = currentIndex();
          const last = items.length - 1;
          const atStart = items.length < 2 || index <= 0;
          const atEnd = items.length < 2 || index >= last;
          prev.toggleAttribute('hidden', atStart);
          next.toggleAttribute('hidden', atEnd);
          prev.disabled = atStart;
          next.disabled = atEnd;
        };

        const activate = (mediaId) => {
          if (typeof gallery.setActiveMedia === 'function') {
            gallery.setActiveMedia(mediaId, false);
          }
          syncArrows();
        };

        const step = (direction) => {
          const items = slides();
          if (items.length < 2) return;
          const index = Math.max(0, Math.min(items.length - 1, currentIndex() + direction));
          const mediaId = items[index]?.dataset.mediaId;
          if (mediaId) activate(mediaId);
        };

        const bind = () => {
          if (this.galleryNavBound) return;
          this.galleryNavBound = true;

          const nav = this.querySelector('[data-gallery-nav]');
          if (nav && viewer && nav.parentElement !== viewer) viewer.appendChild(nav);

          gallery.setActiveMedia = (mediaId) => {
            const activeMedia =
              viewer.querySelector(`[data-media-id="${mediaId}"]`) || viewer.querySelector('[data-media-id]');
            if (!activeMedia) return;

            viewer.querySelectorAll('[data-media-id]').forEach((element) => {
              element.classList.toggle('is-active', element === activeMedia);
            });

            const list = activeMedia.parentElement;
            if (list && window.matchMedia('(max-width: 749px)').matches) {
              list.scrollTo({ left: activeMedia.offsetLeft, behavior: 'auto' });
            }

            if (typeof gallery.playActiveMedia === 'function') {
              gallery.playActiveMedia(activeMedia);
            }

            const thumbnail = gallery.querySelector(`[data-target="${mediaId}"]`);
            if (thumbnail && typeof gallery.setActiveThumbnail === 'function') {
              gallery.setActiveThumbnail(thumbnail);
            }

            syncArrows();
          };

          slides().forEach((slide) => slide.classList.add('scroll-trigger--cancel'));
          const initial =
            slides().find((slide) => slide.classList.contains('is-active')) || slides()[0];
          if (initial?.dataset.mediaId) {
            gallery.setActiveMedia(initial.dataset.mediaId);
          }

          const list = viewer.querySelector('.product__media-list');
          const syncFromScroll = () => {
            if (!list || !window.matchMedia('(max-width: 749px)').matches) return;
            const items = slides();
            if (!items.length) return;

            const left = list.scrollLeft;
            let closest = items[0];
            let closestDist = Infinity;
            items.forEach((item) => {
              const dist = Math.abs(item.offsetLeft - left);
              if (dist < closestDist) {
                closestDist = dist;
                closest = item;
              }
            });

            items.forEach((element) => {
              element.classList.toggle('is-active', element === closest);
            });

            const thumbnail = gallery.querySelector(`[data-target="${closest.dataset.mediaId}"]`);
            if (thumbnail && typeof gallery.setActiveThumbnail === 'function') {
              gallery.setActiveThumbnail(thumbnail);
            }

            syncArrows();
          };

          let scrollFrame = 0;
          list?.addEventListener(
            'scroll',
            () => {
              if (scrollFrame) return;
              scrollFrame = requestAnimationFrame(() => {
                scrollFrame = 0;
                syncFromScroll();
              });
            },
            { passive: true }
          );

          syncArrows();
          prev.addEventListener('click', (event) => {
            event.preventDefault();
            step(-1);
          });
          next.addEventListener('click', (event) => {
            event.preventDefault();
            step(1);
          });
          viewer.addEventListener('slideChanged', (event) => {
            const current = event.detail?.currentElement;
            if (current && window.matchMedia('(max-width: 749px)').matches) {
              slides().forEach((element) => {
                element.classList.toggle('is-active', element === current);
              });
            }
            syncArrows();
          });
          gallery.querySelectorAll('.thumbnail').forEach((thumb) => {
            thumb.addEventListener('click', () => requestAnimationFrame(syncArrows));
          });
        };

        if (customElements.get('media-gallery')) {
          bind();
          return;
        }

        customElements.whenDefined('media-gallery').then(bind);
      }

      bindFaqItems() {
        this.querySelectorAll('.step-gamer-pdp-faq__trigger').forEach((button) => {
          button.addEventListener('click', () => {
            const item = button.closest('.step-gamer-pdp-faq');
            const answer = item?.querySelector('.step-gamer-pdp-faq__answer');
            if (!answer) return;
            const open = button.getAttribute('aria-expanded') === 'true';
            button.setAttribute('aria-expanded', String(!open));
            item.classList.toggle('is-open', !open);
            answer.hidden = open;
          });
        });
      }

      bindAccordions() {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.querySelectorAll('.step-gamer-pdp-acc').forEach((details) => {
          const summary = details.querySelector(':scope > summary');
          const content = details.querySelector(':scope > .step-gamer-pdp-acc__content');
          if (!summary || !content) return;

          summary.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            if (details.dataset.animating === 'true') return;
            const nextOpen = !details.hasAttribute('open');
            if (reduceMotion || typeof content.animate !== 'function') {
              details.toggleAttribute('open', nextOpen);
              return;
            }
            this.animateAccordion(details, content, nextOpen);
          });
        });
      }

      animateAccordion(details, content, open) {
        const duration = open ? 420 : 320;
        const easing = 'cubic-bezier(0.22, 1, 0.36, 1)';
        details.dataset.animating = 'true';

        if (open) {
          details.setAttribute('open', '');
          content.style.overflow = 'hidden';
          content.style.width = '100%';
          content.style.maxWidth = '100%';
          content.style.height = '0px';
          const end = content.scrollHeight;
          const animation = content.animate(
            [
              { height: '0px', opacity: 0.35 },
              { height: `${end}px`, opacity: 1 },
            ],
            { duration, easing, fill: 'forwards' }
          );
          animation.onfinish = () => {
            animation.cancel();
            content.style.height = '';
            content.style.width = '';
            content.style.maxWidth = '';
            content.style.overflow = '';
            details.dataset.animating = '';
          };
          return;
        }

        const start = content.scrollHeight;
        content.style.overflow = 'hidden';
        content.style.width = '100%';
        content.style.maxWidth = '100%';
        const animation = content.animate(
          [
            { height: `${start}px`, opacity: 1 },
            { height: '0px', opacity: 0.35 },
          ],
          { duration, easing, fill: 'forwards' }
        );
        animation.onfinish = () => {
          details.removeAttribute('open');
          animation.cancel();
          content.style.overflow = '';
          content.style.width = '';
          content.style.maxWidth = '';
          details.dataset.animating = '';
        };
      }

      bindPackages() {
        const options = this.querySelectorAll('[data-package-option]');
        if (!options.length) return;

        options.forEach((input) => {
          input.addEventListener('change', () => {
            if (!input.checked) return;
            this.clearPackageError();
            this.applyPackage(input.value);
          });
        });

        const selected = this.querySelector('[data-package-option]:checked');
        this.applyPackage(selected?.value || 'unset');
      }

      applyPackage(name) {
        const isRecommended = name === 'recommended';
        const isNone = name === 'none';
        const isCustom = name === 'custom';
        this.querySelector('[data-packages]')?.setAttribute(
          'data-active',
          isRecommended ? 'recommended' : isNone ? 'none' : isCustom ? 'custom' : 'unset'
        );

        this.querySelectorAll('[data-addon][data-package-item]').forEach((addon) => {
          const input = addon.querySelector('[data-qty-input]');
          if (!input) return;
          input.value = isRecommended ? '1' : '0';
          this.syncAddonState(addon);
        });

        this.updateSubtotal();
      }

      hasPackageSelection() {
        if (!this.querySelector('[data-packages]')) return true;
        return Boolean(this.querySelector('[data-package-option]:checked'));
      }

      showPackageError() {
        const group = this.querySelector('.step-gamer-pdp-packages');
        const error = this.querySelector('[data-package-error]');
        group?.classList.add('is-invalid');
        group?.setAttribute('aria-invalid', 'true');
        if (error) error.hidden = false;

        const headerOffset =
          parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sg-header-height')) || 0;
        const target = this.querySelector('.step-gamer-pdp__accessories-intro') || group;
        if (target) {
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          const top = target.getBoundingClientRect().top + window.scrollY - headerOffset - 16;
          window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? 'auto' : 'smooth' });
        }

        this.querySelector('[data-package-option]')?.focus({ preventScroll: true });
      }

      clearPackageError() {
        const group = this.querySelector('.step-gamer-pdp-packages');
        const error = this.querySelector('[data-package-error]');
        group?.classList.remove('is-invalid');
        group?.removeAttribute('aria-invalid');
        if (error) error.hidden = true;
      }

      bindAddon(addon) {
        const input = addon.querySelector('[data-qty-input]');
        addon.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.changeQty(addon, -1));
        addon.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.changeQty(addon, 1));
        input?.addEventListener('change', () => {
          const max = Number(input.max || 9);
          input.value = String(Math.max(0, Math.min(max, Number(input.value) || 0)));
          this.syncAddonState(addon);
          this.updateSubtotal();
        });
        this.bindInscribe(addon);
        const toggle = addon.querySelector('[data-inscribe-toggle]');
        toggle?.addEventListener('change', () => {
          const qtyInput = addon.querySelector('[data-qty-input]');
          if (qtyInput) qtyInput.value = toggle.checked ? '1' : '0';
          this.syncAddonState(addon);
          this.updateSubtotal();
          if (toggle.checked) {
            addon.querySelector('[data-addon-text]')?.focus();
          }
        });
        this.syncAddonState(addon);
      }

      bindInscribe(addon) {
        const field = addon.querySelector('[data-addon-text]');
        const wrap = addon.querySelector('[data-inscribe]');
        if (!field || !wrap) return;

        const max = Number(field.dataset.maxChars || field.getAttribute('maxlength') || 10);
        const count = wrap.querySelector('[data-inscribe-count]');
        const error = wrap.querySelector('[data-inscribe-error]');
        const countTemplate = count?.dataset.countTemplate || '__USED__ / __MAX__';
        const forbidden = /[^\p{L}\p{N} ]/u;
        const stripForbidden = (value) => value.replace(/[^\p{L}\p{N} ]/gu, '');
        let errorTimer;

        const updateCount = () => {
          const used = field.value.length;
          if (count) {
            count.textContent = countTemplate.replace('__USED__', String(used)).replace('__MAX__', String(max));
          }
          wrap.classList.toggle('is-full', used >= max);
        };

        const showFieldError = (type) => {
          wrap.classList.remove('is-limit');
          void wrap.offsetWidth;
          wrap.classList.add('is-limit');
          if (error) {
            const message =
              type === 'chars'
                ? error.dataset.errorChars
                : error.dataset.errorLimit;
            if (message) error.textContent = message;
            error.dataset.errorType = type === 'chars' ? 'chars' : 'limit';
            error.hidden = false;
          }
          clearTimeout(errorTimer);
          errorTimer = setTimeout(() => {
            wrap.classList.remove('is-limit');
            if (error) error.hidden = true;
          }, 2600);
        };

        const wouldExceed = (incoming = '', replaceCount = 0) =>
          field.value.length - replaceCount + incoming.length > max;

        field.addEventListener('keydown', (event) => {
          if (event.metaKey || event.ctrlKey || event.altKey) return;
          if (event.key.length !== 1) return;
          if (forbidden.test(event.key)) {
            event.preventDefault();
            showFieldError('chars');
            return;
          }
          const selected = (field.selectionEnd || 0) - (field.selectionStart || 0);
          if (wouldExceed(event.key, selected)) {
            event.preventDefault();
            showFieldError('limit');
          }
        });

        field.addEventListener('paste', (event) => {
          event.preventDefault();
          const raw = event.clipboardData?.getData('text') || '';
          const pasted = stripForbidden(raw);
          const start = field.selectionStart || 0;
          const end = field.selectionEnd || 0;
          const selected = end - start;
          const hadForbidden = raw !== pasted;
          let next = pasted;
          let overLimit = false;
          if (wouldExceed(pasted, selected)) {
            const room = Math.max(0, max - (field.value.length - selected));
            next = pasted.slice(0, room);
            overLimit = true;
          }
          field.setRangeText(next, start, end, 'end');
          updateCount();
          if (hadForbidden) showFieldError('chars');
          else if (overLimit) showFieldError('limit');
        });

        field.addEventListener('input', () => {
          const cleaned = stripForbidden(field.value);
          if (cleaned !== field.value) {
            field.value = cleaned;
            showFieldError('chars');
          }
          if (field.value.length > max) {
            field.value = field.value.slice(0, max);
            showFieldError('limit');
          }
          updateCount();
        });

        updateCount();
      }

      changeQty(addon, delta) {
        const input = addon.querySelector('[data-qty-input]');
        if (!input) return;
        const max = Number(input.max || 9);
        const next = Math.max(0, Math.min(max, (Number(input.value) || 0) + delta));
        input.value = String(next);
        this.syncAddonState(addon);
        this.updateSubtotal();
      }

      syncAddonState(addon) {
        const toggle = addon.querySelector('[data-inscribe-toggle]');
        const qty = Number(addon.querySelector('[data-qty-input]')?.value || 0);
        const enabled = toggle ? toggle.checked : qty > 0;
        addon.classList.toggle('is-selected', enabled);
        const inscribe = addon.querySelector('[data-inscribe]');
        const text = addon.querySelector('[data-addon-text]');
        if (inscribe) inscribe.hidden = !enabled;
        else if (text) text.hidden = !enabled;
      }

      onVariantChange(event) {
        if (event.data.sectionId !== this.dataset.section) return;
        const variant = event.data.variant;
        if (!variant) return;

        this.variantPrice = Number(variant.price || 0);
        this.dataset.variantPrice = String(this.variantPrice);

        const html = event.data.html;
        const chosenSource = html.getElementById(`PdpChosen-${this.dataset.section}`);
        const chosenDest = this.querySelector(`#PdpChosen-${this.dataset.section}`);
        if (chosenSource && chosenDest) chosenDest.innerHTML = chosenSource.innerHTML;

        if (this.sizeOptionPosition && Array.isArray(variant.options)) {
          const sizeValue = variant.options[this.sizeOptionPosition - 1];
          const dimension = this.querySelector('[data-pdp-dimension]');
          if (dimension && sizeValue) dimension.textContent = sizeValue;
        }

        this.paytrLoadedAmount = '';
        this.updateSubtotal();
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;
        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'tr-TR', {
            style: 'currency',
            currency: this.currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount);
        } catch (error) {
          return `${amount.toFixed(2)} ${this.currency}`;
        }
      }

      getAddonTotal() {
        return Array.from(this.querySelectorAll('[data-addon]')).reduce((sum, addon) => {
          const qty = Number(addon.querySelector('[data-qty-input]')?.value || 0);
          const price = Number(addon.dataset.price || 0);
          return sum + price * qty;
        }, 0);
      }

      updateSubtotal() {
        const subtotal = this.variantPrice + this.getAddonTotal();
        const formatted = this.formatMoney(subtotal);
        const nodes = [...this.querySelectorAll('[data-pdp-subtotal]')];
        this.stickyBar?.querySelectorAll('[data-pdp-subtotal]').forEach((node) => nodes.push(node));
        nodes.forEach((node) => {
          node.textContent = formatted;
        });

        const dialog = this.querySelector('[data-taksit-dialog]');
        if (dialog?.open) this.loadPaytrTable();
      }

      collectItems() {
        const variantId = this.form?.querySelector('input[name="id"]')?.value;
        const items = [];
        if (variantId) {
          items.push({ id: Number(variantId), quantity: 1 });
        }

        this.querySelectorAll('[data-addon]').forEach((addon) => {
          const qty = Number(addon.querySelector('[data-qty-input]')?.value || 0);
          if (qty < 1) return;
          const textInput = addon.querySelector('[data-addon-text]');
          const maxChars = Number(textInput?.dataset.maxChars || 10);
          const text = textInput?.value?.trim().slice(0, maxChars);
          const variantId = Number(addon.dataset.variantId || 0);
          if (!variantId) {
            if (text && items[0]) {
              items[0].properties = {
                ...(items[0].properties || {}),
                [textInput.dataset.propertyName || 'Custom']: text,
              };
            }
            return;
          }
          const item = { id: variantId, quantity: qty };
          if (text) {
            item.properties = {
              [textInput.dataset.propertyName || 'Custom']: text,
            };
            const parentTitle = this.dataset.productTitle;
            const parentKey = textInput.dataset.parentProperty;
            if (parentTitle && parentKey) {
              item.properties[parentKey] = parentTitle;
            }
          }
          items.push(item);
        });

        return items;
      }

      setLoading(isLoading) {
        this.classList.toggle('is-loading', isLoading);
        const buttons = [...this.querySelectorAll('[data-pdp-submit]')];
        this.stickyBar?.querySelectorAll('[data-pdp-sticky-submit]').forEach((button) => buttons.push(button));
        buttons.forEach((button) => {
          button.classList.toggle('loading', isLoading);
          button.toggleAttribute('aria-disabled', isLoading);
          button.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
        });
      }

      isBuyNowEvent(event) {
        const target = event.submitter || event.target;
        return Boolean(target?.closest?.('.shopify-payment-button'));
      }

      onBuyNowClick(event) {
        if (!this.isBuyNowEvent(event)) return;
        if (this.hasPackageSelection()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showPackageError();
      }

      onSubmit(event) {
        if (this.isBuyNowEvent(event)) {
          event.stopImmediatePropagation();
          if (!this.hasPackageSelection()) {
            event.preventDefault();
            this.showPackageError();
          }
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.classList.contains('is-loading')) return;

        if (!this.hasPackageSelection()) {
          this.showPackageError();
          return;
        }

        const items = this.collectItems();
        if (!items.length) return;

        const textAddon = this.querySelector('[data-addon][data-enable-text="true"]');
        if (textAddon) {
          const toggle = textAddon.querySelector('[data-inscribe-toggle]');
          const textInput = textAddon.querySelector('[data-addon-text]');
          const wrap = textAddon.querySelector('[data-inscribe]');
          const error = textAddon.querySelector('[data-inscribe-error]');
          if (toggle?.checked && !textInput?.value.trim()) {
            wrap?.removeAttribute('hidden');
            if (error) {
              error.textContent = textInput.dataset.requiredMessage || error.dataset.errorLimit;
              error.dataset.errorType = 'required';
              error.hidden = false;
            }
            textInput?.focus();
            return;
          }
        }

        this.setLoading(true);
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData();
        items.forEach((item, index) => {
          formData.append(`items[${index}][id]`, String(item.id));
          formData.append(`items[${index}][quantity]`, String(item.quantity));
          if (item.properties) {
            Object.entries(item.properties).forEach(([key, value]) => {
              formData.append(`items[${index}][properties][${key}]`, value);
            });
          }
        });

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement?.(document.activeElement);
        }

        config.body = formData;

        fetch(window.routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.productForm?.handleErrorMessage(response.description || response.message);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'step-gamer-pdp',
                productVariantId: items[0]?.id,
                errors: response.errors || response.description,
                message: response.message,
              });
              return;
            }

            this.productForm?.handleErrorMessage();

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'step-gamer-pdp',
              productVariantId: items[0]?.id,
              cartData: response,
            });

            if (window.StepGamerCart) {
              window.StepGamerCart.refresh().then(() => window.StepGamerCart.open());
            } else if (this.cart) {
              this.cart.renderContents(response);
              this.cart.classList.remove('is-empty');
            } else {
              window.location = window.routes.cart_url;
            }
          })
          .catch((error) => {
            console.error(error);
            this.productForm?.handleErrorMessage(error.message);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }
    }
  );
}
